import { DatasetJsonLd, WebPageJsonLd } from "next-seo";
import { addBaseUrl } from "~/modules/app/seo/addBaseUrl";
import type { Measure } from "~/modules/measures/types";
import { getMeasureURL } from "~/routes";

// // // //

export function getMetricPageTitle(props: { measure: Measure }): string {
    const { measure } = props;
    return `${measure.display_name} | RWA Data Catalog`;
}

export function getMetricPageDescription(props: { measure: Measure }): string {
    const { measure } = props;

    if (measure.description) {
        return measure.description;
    }

    const parts = [
        `Explore the ${measure.display_name} metric in the RWA.xyz data catalog.`,
    ];

    if (measure.asset_count > 0) {
        parts.push(
            `Available for ${measure.asset_count.toLocaleString()} assets`,
        );
    }

    if (measure.network_count > 0) {
        parts.push(`across ${measure.network_count.toLocaleString()} networks`);
    }

    return parts.join(" ") + ".";
}

// // // //

/**
 * Renders WebPage + Dataset + DefinedTerm JSON-LD structured data
 * for the Metric Individual page.
 */
export function MetricIndividualSEO(props: { measure: Measure }) {
    const { measure } = props;

    const pageUrl = addBaseUrl(getMeasureURL({ measureSlug: measure.slug }));
    const description = getMetricPageDescription({ measure });

    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const isoYesterday = yesterday.toISOString();

    // DefinedTerm JSON-LD: represents a defined metric/concept in a taxonomy
    // Not available as a next-seo primitive, so we render it manually.
    const definedTermJsonLd = {
        "@context": "https://schema.org",
        "@type": "DefinedTerm",
        "@id": pageUrl,
        name: measure.display_name,
        description,
        url: pageUrl,
        ...(measure.unit && {
            termCode: measure.unit,
        }),
        inDefinedTermSet: {
            "@type": "DefinedTermSet",
            name: "RWA.xyz Data Catalog",
            url: addBaseUrl("/catalog"),
        },
    };

    return (
        <>
            <WebPageJsonLd
                id={pageUrl}
                description={description}
                lastReviewed={isoYesterday}
                reviewedBy={{
                    type: "Organization",
                    name: "RWA.xyz",
                }}
            />
            <DatasetJsonLd
                name={`${measure.display_name} — RWA.xyz Metric`}
                description={description}
            />
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify(definedTermJsonLd),
                }}
            />
        </>
    );
}
